import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
} from '@tonic/shared/components/ui';
import { Plus, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { useResaleMetrics } from '@/features/re-commerce/api/hooks';
import { formatCurrency, formatNumber } from '@/utils/format';
import { TimeSeriesChart } from '../components/charts/TimeSeriesChart';

export function ResaleOverviewPage() {
  const { data: metricsData, isLoading } = useResaleMetrics();

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="h-8 w-48 bg-neutral-800 animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="bg-neutral-800 animate-pulse h-32" />
          ))}
        </div>
      </div>
    );
  }

  // Default metrics if data is not available
  const metrics = metricsData?.resaleMetrics || {
    revenue: { value: 0, change: 0, trend: 0 },
    itemsListed: { value: 0, change: 0, trend: 0 },
    timeToList: { value: 0, change: 0, trend: 0 },
    profitMargin: { value: 0, change: 0, trend: 0 },
    revenueChart: [],
    categoryChart: []
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Resale Overview</h1>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          New Resale Item
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <Badge variant={metrics.revenue.trend > 0 ? 'secondary' : 'destructive'}>
              {metrics.revenue.trend > 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {Math.abs(metrics.revenue.trend)}%
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(metrics.revenue.value)}</div>
            <p className="text-xs text-neutral-500">+{formatCurrency(metrics.revenue.change)} from last month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Items Listed</CardTitle>
            <Badge variant={metrics.itemsListed.trend > 0 ? 'secondary' : 'destructive'}>
              {metrics.itemsListed.trend > 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {Math.abs(metrics.itemsListed.trend)}%
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(metrics.itemsListed.value)}</div>
            <p className="text-xs text-neutral-500">+{metrics.itemsListed.change} from last month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Time to List</CardTitle>
            <Badge variant={metrics.timeToList.trend < 0 ? 'secondary' : 'destructive'}>
              {metrics.timeToList.trend < 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {Math.abs(metrics.timeToList.trend)}%
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.timeToList.value} days</div>
            <p className="text-xs text-neutral-500">{metrics.timeToList.change} days from last month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Profit Margin</CardTitle>
            <Badge variant={metrics.profitMargin.trend > 0 ? 'secondary' : 'destructive'}>
              {metrics.profitMargin.trend > 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {Math.abs(metrics.profitMargin.trend)}%
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.profitMargin.value}%</div>
            <p className="text-xs text-neutral-500">{metrics.profitMargin.change}% from last month</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TimeSeriesChart
          title="Revenue Over Time"
          type="line"
          data={metrics.revenueChart}
          dataKey="value"
          xAxisKey="date"
          height={300}
          colors={['#0058AB']}
        />

        <TimeSeriesChart
          title="Items by Category"
          type="bar"
          data={metrics.categoryChart}
          dataKey="count"
          xAxisKey="category"
          height={300}
          colors={['#0058AB']}
        />
      </div>
    </div>
  );
}
